// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Clickable from "@insite/mobius/Clickable";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import QuestionMarkCircleFill from "@store/Library/Components/Toolkit/Icons/QuestionMarkCircle/QuestionMarkCircleFill";
import { infoProps } from "@store/Themes/common/props/info";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps extends ExpandStyles<ProductDeliveryTermsStyles> {
    id: string;
    displayTexts?: boolean;
    displaySeparator?: boolean;
    termsAndConditionsLink?: LinkFieldValue;
    overrideLinkLabel?: string;
}

type Props = OwnProps & HasProduct;

interface DeliveryTermsMessage {
    text?: TypographyPresentationProps;
}

interface DeliveryTermModel {
    deliveryTerms?: string;
    deliveryTermsDescription?: string;
}

export interface ProductDeliveryTermsStyles {
    deliveryTermsMessage?: DeliveryTermsMessage;
    additionalInfoLabel?: TypographyPresentationProps;
    additionalInfoText?: TypographyPresentationProps;
    deliveryTermsWrapper?: InjectableCss;
    pipeStyles?: TypographyPresentationProps;
    infoIcon?: IconProps;
    modal?: ModalPresentationProps;
    terms?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    descriptionText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

export const productDeliveryTermsStyles: ProductDeliveryTermsStyles = {
    deliveryTermsMessage: {
        text: {
            css: css`
                font-size: ${x => x.theme.design.typography.size.smaller};
            `,
        },
    },
    deliveryTermsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            gap: ${pxToRem(4)};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    additionalInfoLabel: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    pipeStyles: {
        color: "text.disabled",
        css: css`
            margin: ${pxToRem(0, 6)};
        `,
    },
    infoIcon: {
        src: QuestionMarkCircleFill,
        size: 16,
    },
    modal: {
        sizeVariant: "small",
    },
    wrapper: {
        css: css`
            ${spacingStyles(null, pxToRem(16))}
        `,
    },
    terms: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    titleText: infoProps.value,
    descriptionText: infoProps.label,
};

const ProductDeliveryTerms = ({
    id,
    product,
    extendedStyles,
    overridedStyles,
    displayTexts = true,
    displaySeparator = false,
    termsAndConditionsLink,
    overrideLinkLabel,
}: Props) => {
    const styles = useExpandProps(productDeliveryTermsStyles, extendedStyles, overridedStyles);
    const { deliveryTerms } = product.properties;
    const link = termsAndConditionsLink ? useGetLink(termsAndConditionsLink) : undefined;

    // Receiving data as a JSON string, needs to convert and extract properties
    const deliveryTermsObject = JSON.parse(deliveryTerms);

    const [deliveryTermsData, setDeliveryTermsData] = useState<DeliveryTermModel>({});
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    if (deliveryTermsObject.length === 0) {
        return null;
    }

    useEffect(() => {
        if (deliveryTermsObject) {
            setDeliveryTermsData({
                deliveryTerms: deliveryTermsObject.DeliveryTerms,
                deliveryTermsDescription: deliveryTermsObject.DeliveryTermsDescription,
            });
        }
    }, [deliveryTermsObject]);

    const openModalHandler = () => {
        setModalIsOpen(true);
    };

    const closeModalHandler = () => {
        setModalIsOpen(false);
    };

    return (
        <>
            {displaySeparator && <Typography {...styles.pipeStyles}>|</Typography>}
            <StyledWrapper {...styles.deliveryTermsWrapper}>
                <Typography {...styles.additionalInfoLabel}>
                    {displayTexts && `${translate("Delivery Terms")}${translate(":")}`}
                </Typography>
                <Typography {...styles.additionalInfoText}>{` ${deliveryTermsData?.deliveryTerms}`}</Typography>
                {deliveryTermsData?.deliveryTermsDescription && (
                    <Clickable onClick={openModalHandler}>
                        <Icon {...styles.infoIcon} />
                    </Clickable>
                )}
            </StyledWrapper>
            <Modal
                {...styles.modal}
                headline={translate("Delivery Terms")}
                isOpen={modalIsOpen}
                handleClose={closeModalHandler}
            >
                <StyledWrapper {...styles.wrapper}>
                    <Typography as="h3" {...styles.titleText}>
                        {translate("Delivery Terms Description")}
                    </Typography>
                    <Typography {...styles.descriptionText}>
                        {deliveryTermsData?.deliveryTermsDescription} ({deliveryTermsData?.deliveryTerms})
                    </Typography>
                </StyledWrapper>
                {link && (
                    <StyledWrapper {...styles.wrapper}>
                        <Typography>{translate("For more information, see our ")}</Typography>
                        <Link href={link.url}>{overrideLinkLabel || link.title}</Link>
                    </StyledWrapper>
                )}
            </Modal>
        </>
    );
};

export default withProduct(ProductDeliveryTerms);
